/* eslint-disable eslint-comments/disable-enable-pair, react/no-set-state */
import React, { Component } from 'react';
import debounce from 'lodash.debounce';

import { autocompleteDefaultProperties, autocompletePropertyTypes } from 'src/types/autocomplete';
import Autocomplete from 'src/components/Search/Autocomplete/Autocomplete';


const keyCodes = {
  up: 38,
  down: 40,
  enter: 13,
  backspace: 8
};

const DEBOUNCE_AUTOCOMPLETE_FETCH_LIST = 200;

class AutocompleteContainer extends Component {
  state = {
    selected: 0
  };

  componentDidMount() {
    const { actions } = this.props;
    actions.autocomplete.generate();
  }

  selectFromAutocompleteValueIfPossible = (notAbleToUseAutocompleteSuggestionHandler = () => {}) => {
    const {
      autocompleteParams: { suggestions, value },
      actions: { autocomplete: { addTag } }
    } = this.props;

    const matchedSuggestions = this.matchingTags(suggestions, 'text', value);

    const isOnlyOneMatchedSuggestionAvailable = matchedSuggestions.length === 1;
    if (isOnlyOneMatchedSuggestionAvailable) {
      const matchedSuggestionIndex = suggestions.indexOf(matchedSuggestions[0]);
      if(matchedSuggestionIndex !== -1) {
        addTag(matchedSuggestionIndex + 1);
        this.autocompleteClear();
        return;
      }
    }

    notAbleToUseAutocompleteSuggestionHandler();
  }

  matchingTags = (tags, key, value) => {
    const matchingValue = this.normalizeString(value);
    const matchedTags = tags
      .filter((tag) => this.normalizeString(tag[key]) === matchingValue);
    return matchedTags;
  }

  normalizeString = string =>
    string
      .toLowerCase()
      .replace(' ', '')
      .normalize('NFD')
      // eslint-disable-next-line require-unicode-regexp
      .replace(/[\u0300-\u036f]/g, '')
      // needed, because string.normalize() omits "ł"
      // eslint-disable-next-line require-unicode-regexp
      .replace(/ł/g, 'l');

  debounceAutocompleteFetchList = debounce((value) => {
    const { actions: { autocomplete: { fetchList } } } = this.props;
    fetchList(value);
  }, DEBOUNCE_AUTOCOMPLETE_FETCH_LIST);

  autocompleteFill = (event) => {
    const { actions: { autocomplete: { show, updateValue } } } = this.props;
    const { value } = event.target;
    show();
    this.debounceAutocompleteFetchList(value);
    updateValue(value);
    this.setState({
      selected: 0
    });
  };

  autocompleteMoveUp = () => {
    const { selected } = this.state;
    this.setState({
      selected: selected - 1 <= 0 ? 1 : selected - 1
    });
  };

  autocompleteMoveDown = () => {
    const { selected } = this.state;
    const { autocompleteParams } = this.props;
    const { length } = autocompleteParams.suggestions;
    this.setState({
      selected: selected >= length ? selected : selected + 1
    });
  };

  autocompleteEnter = (event) => { // eslint-disable-line max-statements
    const {
      showAutocomplete,
      autocompleteParams: { value },
      actions: { autocomplete: { addTag, addCustomTag } }
    } = this.props;

    const { selected } = this.state;

    if (!showAutocomplete) {
      return false;
    }

    const valueNotEmpty = value.length > 0;

    if (selected) {
      event.preventDefault();
      addTag(selected);
      this.autocompleteClear();
      this.autocompleteDisable();
      return true;
    }
    if (valueNotEmpty) {
      event.preventDefault();
      this.selectFromAutocompleteValueIfPossible(
        () => {
          addCustomTag(value)
          this.autocompleteClear();
        }
      );
      this.autocompleteDisable();
      return true;
    }
    return false;
  };

  autocompleteRemoveLast = () => {
    const { autocompleteParams: { tokens, value }, actions: { autocomplete: { removeTag } } } = this.props;
    if (value.length === 0) {
      removeTag(tokens[tokens.length - 1]);
    }
  };

  autocompleteKeys = (event) => {
    switch (event.keyCode) {
      case keyCodes.up:
        this.autocompleteMoveUp();
        break;
      case keyCodes.down:
        this.autocompleteMoveDown();
        break;
      case keyCodes.enter:
        this.autocompleteEnter(event);
        break;
      case keyCodes.backspace:
        this.autocompleteRemoveLast();
        break;
      default:
        break;
    }
  };

  autocompleteEnable = () => {
    const { autocompleteParams: { value }, actions: { autocomplete: { show } } } = this.props;
    this.debounceAutocompleteFetchList(value);
    show();
    this.setState({
      selected: 0
    });
  };

  autocompleteDisable = () => {
    const { actions: { autocomplete: { hide } } } = this.props;
    hide();
    this.setState({
      selected: 0
    });
  };

  autocompleteBlur = (event) => {
    const blurTarget = event.relatedTarget || document.activeElement;

    if (blurTarget && blurTarget.hasAttribute('data-target') && blurTarget.dataset.target === 'autocomplete-item') {
      event.preventDefault();
      this.autocompleteDisable();
      return;
    }

    const {
      autocompleteParams: { value },
      actions: { autocomplete: { addCustomTag } }
    } = this.props;

    if (value.length > 0) {
      this.selectFromAutocompleteValueIfPossible(
        () => {
          addCustomTag(value)
          this.autocompleteClear();
        }
      );
    }
    this.autocompleteDisable();
  };

  autocompleteClear = () => {
    const { actions: { autocomplete: { updateValue } } } = this.props;
    updateValue();
  };

  autocompleteActions = {
    autocompleteFill: this.autocompleteFill,
    autocompleteKeys: this.autocompleteKeys,
    autocompleteEnable: this.autocompleteEnable,
    autocompleteDisable: this.autocompleteDisable,
    autocompleteBlur: this.autocompleteBlur,
    removeTag: this.props.actions.autocomplete.removeTag, //  eslint-disable-line react/destructuring-assignment
    addTag: (tag) => {
      this.props.actions.autocomplete.addTag(tag); //  eslint-disable-line react/destructuring-assignment
      this.autocompleteClear();
    }
  };

  render() {
    const {
      autocompleteParams: { suggestions, tokens, value }, showAutocomplete, name, placeholder
    } = this.props;
    const { selected } = this.state;

    const autocomplete = {
      list: suggestions,
      show: showAutocomplete,
      tokens,
      selected,
      value
    };

    return (
      <Autocomplete
        actions={ this.autocompleteActions }
        autocomplete={ autocomplete }
        name={ name }
        placeholder={ placeholder }
      />
    );
  }
}

AutocompleteContainer.propTypes = autocompletePropertyTypes;

AutocompleteContainer.defaultProps = autocompleteDefaultProperties;

export default AutocompleteContainer;
